import { useState, useEffect, useCallback, FormEvent } from 'react'
import { ultraadminApi, School, SuperadminInfo, SchoolDomain, SchoolUser } from '@/services/api/ultraadmin'
import { Button, LoadingSpinner, Input, Select, Modal } from '@/components/common'
import { Plus, Trash2, Repeat2, X, AlertTriangle, Power } from 'lucide-react'
import { extractErrorMessage } from '@/utils/errorHandler'

export function SuperuserManagementPage() {
  // Data state
  const [schools, setSchools] = useState<School[]>([])
  const [selectedSchoolId, setSelectedSchoolId] = useState('')
  const [superadmins, setSuperadmins] = useState<SuperadminInfo[]>([])
  const [domains, setDomains] = useState<SchoolDomain[]>([])
  const [transferTargets, setTransferTargets] = useState<SchoolUser[]>([])

  // UI state — separate initial vs data loading
  const [initialLoading, setInitialLoading] = useState(true)
  const [dataLoading, setDataLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<SuperadminInfo | null>(null)

  // Form state
  const [createForm, setCreateForm] = useState({ email: '', first_name: '', last_name: '' })
  const [transferTargetId, setTransferTargetId] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  // Load schools on mount
  useEffect(() => {
    ultraadminApi.listSchools({ is_active: true })
      .then((data) => setSchools(data))
      .catch((err: unknown) => {
        const errorMessage = extractErrorMessage(err, {
          loadError: 'Schulen konnten nicht geladen werden',
          permissionError: 'Keine Berechtigung. Ultraadmin-Rolle erforderlich.',
        })
        setError(errorMessage)
      })
      .finally(() => setInitialLoading(false))
  }, [])

  // Load superadmins + domains when school selected
  const loadSchoolData = useCallback(async (schoolId: string) => {
    if (!schoolId) {
      setSuperadmins([])
      setDomains([])
      setTransferTargets([])
      return
    }
    setDataLoading(true)
    setError(null)
    try {
      const [admins, schoolDomains] = await Promise.all([
        ultraadminApi.listSuperadmins(schoolId),
        ultraadminApi.getSchoolDomains(schoolId),
      ])
      setSuperadmins(admins)
      setDomains(schoolDomains)
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Daten konnten nicht geladen werden',
        permissionError: 'Keine Berechtigung. Ultraadmin-Rolle erforderlich.',
      })
      console.error('Failed to load school data:', err)
      setError(errorMessage)
    } finally {
      setDataLoading(false)
    }
  }, [])

  // Load transfer targets
  const loadTransferTargets = useCallback(async (schoolId: string) => {
    if (!schoolId) return
    try {
      const res = await ultraadminApi.getSchoolUsers(schoolId, { exclude_role: 'superadmin', is_active: true })
      setTransferTargets(res.users)
    } catch {
      setTransferTargets([])
    }
  }, [])

  const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSchoolId(e.target.value)
    loadSchoolData(e.target.value)
  }

  // Auto-dismiss success after 5s
  useEffect(() => {
    if (!success) return
    const t = setTimeout(() => setSuccess(null), 5000)
    return () => clearTimeout(t)
  }, [success])

  // -- Create handler --
  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)

    const emailDomain = createForm.email.split('@')[1]?.toLowerCase()
    const validDomains = domains.map((d) => d.domain.toLowerCase())
    if (!emailDomain || !validDomains.includes(emailDomain)) {
      setFormError(`Die Email muss mit einer der Schul-Domains enden: ${validDomains.join(', ')}`)
      return
    }

    setSubmitting(true)
    try {
      await ultraadminApi.createSuperadminForSchool(selectedSchoolId, createForm)
      setSuccess('Superuser erstellt.')
      setShowCreateModal(false)
      setCreateForm({ email: '', first_name: '', last_name: '' })
      await loadSchoolData(selectedSchoolId)
    } catch (err: any) {
      setFormError(err?.detail || 'Fehler beim Erstellen')
    } finally {
      setSubmitting(false)
    }
  }

  // -- Delete handler --
  const handleDelete = async () => {
    if (!selectedUser) return
    setSubmitting(true)
    try {
      await ultraadminApi.deleteSuperadmin(selectedUser.id)
      setSuccess('Superadmin deaktiviert.')
      setShowDeleteDialog(false)
      setSelectedUser(null)
      await loadSchoolData(selectedSchoolId)
    } catch (err: any) {
      setError(err?.detail || 'Fehler beim Löschen')
      setShowDeleteDialog(false)
    } finally {
      setSubmitting(false)
    }
  }

  // -- Activate handler --
  const handleActivate = async (admin: SuperadminInfo) => {
    setSubmitting(true)
    try {
      await ultraadminApi.activateSuperadmin(admin.id)
      setSuccess('Superadmin aktiviert.')
      await loadSchoolData(selectedSchoolId)
    } catch (err: any) {
      setError(err?.detail || 'Fehler beim Aktivieren')
    } finally {
      setSubmitting(false)
    }
  }

  // -- Transfer handler --
  const handleTransfer = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (!selectedUser || !transferTargetId) {
      setFormError('Bitte wählen Sie einen Ziel-User aus.')
      return
    }

    setSubmitting(true)
    try {
      await ultraadminApi.transferSuperadmin(selectedUser.id, { target_user_id: transferTargetId })
      setSuccess('Superadmin-Rechte erfolgreich übertragen.')
      setShowTransferModal(false)
      setSelectedUser(null)
      setTransferTargetId('')
      await loadSchoolData(selectedSchoolId)
    } catch (err: any) {
      setFormError(err?.detail || 'Fehler beim Übertragen')
    } finally {
      setSubmitting(false)
    }
  }

  // Open transfer modal: load targets
  const openTransferModal = (admin: SuperadminInfo) => {
    setSelectedUser(admin)
    setTransferTargetId('')
    setFormError(null)
    setShowTransferModal(true)
    loadTransferTargets(admin.school_id)
  }

  const activeCount = superadmins.filter((a) => a.is_active).length

  const schoolOptions = schools.map((s) => ({
    value: s.id,
    label: s.city ? `${s.name} (${s.city})` : s.name,
  }))

  const transferOptions = transferTargets.map((u) => ({
    value: u.id,
    label: u.last_name ? `${u.first_name ?? ''} ${u.last_name} (${u.email})` : u.email,
  }))

  if (initialLoading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Superuser Management</h1>
        <p className="mt-1 text-gray-500">Superadmin-Accounts erstellen, löschen und Rechte übertragen</p>
      </div>

      {/* Error / Success */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X size={16} />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700">
            <X size={16} />
          </button>
        </div>
      )}

      {/* School Selector */}
      <div className="mb-6">
        <Select
          label="Schule auswählen"
          value={selectedSchoolId}
          onChange={handleSchoolChange}
          options={schoolOptions}
          placeholder="— Schule wählen —"
        />
      </div>

      {/* Loading indicator for data */}
      {dataLoading && (
        <div className="flex justify-center py-4"><LoadingSpinner size="md" /></div>
      )}

      {/* Create Button */}
      {selectedSchoolId && !dataLoading && (
        <div className="mb-4">
          <Button variant="primary" onClick={() => { setShowCreateModal(true); setFormError(null) }}>
            <Plus size={16} className="mr-2" /> Neuen Superuser erstellen
          </Button>
        </div>
      )}

      {/* Superadmin Table */}
      {selectedSchoolId && !dataLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium text-gray-600">Name</th>
                <th className="text-left p-3 font-medium text-gray-600">Email</th>
                <th className="text-left p-3 font-medium text-gray-600">Status</th>
                <th className="text-left p-3 font-medium text-gray-600">Erstellt</th>
                <th className="text-right p-3 font-medium text-gray-600">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {superadmins.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-gray-500">
                    Keine Superadmins für diese Schule gefunden
                  </td>
                </tr>
              ) : (
                superadmins.map((admin) => (
                  <tr key={admin.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{[admin.first_name, admin.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td className="p-3 text-sm text-gray-600">{admin.email}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        admin.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {admin.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-gray-500">
                      {new Date(admin.created_at).toLocaleDateString('de-CH')}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-2 justify-end">
                        {admin.is_active ? (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openTransferModal(admin)}
                              disabled={activeCount <= 1}
                              title={activeCount <= 1 ? 'Nur aktive Superadmins können übertragen werden' : 'Rechte übertragen'}
                            >
                              <Repeat2 size={14} />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => { setSelectedUser(admin); setShowDeleteDialog(true) }}
                              title="Superadmin deaktivieren"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleActivate(admin)}
                            title="Superadmin aktivieren"
                          >
                            <Power size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setFormError(null) }} title="Neuen Superuser erstellen" size="md">
        {formError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{formError}</div>}
        <form onSubmit={handleCreate}>
          <div className="space-y-4">
            <Input
              label="Vorname"
              value={createForm.first_name}
              onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
              required
              placeholder="Max"
            />
            <Input
              label="Nachname"
              value={createForm.last_name}
              onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
              required
              placeholder="Muster"
            />
            <Input
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              required
              placeholder="max.muster@schul-domain.ch"
            />
            {domains.length > 0 && (
              <p className="text-xs text-gray-500">
                Gültige Domains: {domains.map((d) => d.domain).join(', ')}
              </p>
            )}
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <Button variant="secondary" type="button" onClick={() => setShowCreateModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" type="submit" isLoading={submitting}>
              Erstellen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirm Dialog */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Superadmin löschen" size="sm">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={20} />
          <p className="text-gray-700 text-sm">
            Sind Sie sicher, dass Sie <strong>{[selectedUser?.first_name, selectedUser?.last_name].filter(Boolean).join(' ') || selectedUser?.email}</strong> als Superadmin löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </div>
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={() => setShowDeleteDialog(false)} disabled={submitting}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={submitting}>
            Löschen
          </Button>
        </div>
      </Modal>

      {/* Transfer Modal */}
      <Modal isOpen={showTransferModal} onClose={() => { setShowTransferModal(false); setFormError(null) }} title="Superadmin-Rechte übertragen" size="md">
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          <strong>Warnung:</strong> Die Superadmin-Rechte werden von {[selectedUser?.first_name, selectedUser?.last_name].filter(Boolean).join(' ') || selectedUser?.email} übertragen.
          Der aktuelle Superadmin wird zum Lehrer herabgestuft.
        </div>
        {formError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{formError}</div>}
        <form onSubmit={handleTransfer}>
          <Select
            label="Ziel-User"
            value={transferTargetId}
            onChange={(e) => setTransferTargetId(e.target.value)}
            options={transferOptions}
            placeholder="— Ziel-User wählen —"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Nur aktive Users derselben Schule (ohne Superadmin-Rolle) werden angezeigt.
          </p>
          <div className="flex gap-3 justify-end mt-6">
            <Button variant="secondary" type="button" onClick={() => setShowTransferModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" type="submit" isLoading={submitting} disabled={!transferTargetId}>
              Übertragen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
